import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { LoggerService } from './logger.service';
import { ConfigurationService } from './configuration.service';
import { CHANNEL_TYPES, INTERACTION_STATES } from '../api/AmcApi';
import { ApiService } from './api.service';
import { AgentLockoutReason, AgentLockoutService } from './agent-lockout.service';
import { IConsumptionEntry } from '../model/IConsumptionEntry';
import { IAddInteractionResponse } from '../model/IAddInteractionResponse';
import { IConsumptionConfiguration } from '../model/IConsumptionConfiguration';

const DEFAULT_INTERACTION_TO_MAPPING = ['to', 'To', 'DNIS', 'dnis', 'dialedPhone', 'DialedPhone'];
const DEFAULT_INTERACTION_FROM_MAPPING = ['from', 'From', 'ANI', 'ani', 'phone', 'Phone'];

@Injectable({
  providedIn: 'root'
})
export class InteractionConsumptionService {
  private API_URL: string;
  private API_VERSION = 'v1';
  private regionalApiBaseUrl: string | null = null;
  private readonly STORAGE_KEY = 'Consumption';
  private readonly CLEAR_TIMEOUT_MS = 30000; // 30 seconds default
  private consumptionMap: Map<string, IConsumptionEntry> = new Map();
  private clearTimeouts: Map<string, ReturnType<typeof setTimeout>> = new Map();
  private lockoutPendingScenarios: Set<string> = new Set();
  private scenarioInteractionStates: Map<string, Map<string, boolean>> = new Map();
  private interactionFromMapping: string[] = [...DEFAULT_INTERACTION_FROM_MAPPING];
  private interactionToMapping: string[] = [...DEFAULT_INTERACTION_TO_MAPPING];

  constructor(
    private http: HttpClient,
    private loggerService: LoggerService,
    private configService: ConfigurationService,
    private apiService: ApiService,
    private agentLockoutService: AgentLockoutService
  ) {
    this.loadFromStorage();
  }

  initialize(): void {
    this.API_URL = this.configService.config.apiUrl;
    this.API_VERSION = this.configService.config.apiVersion;
  }

  async initializeProfileConfiguration(configPlugins: any[]): Promise<boolean> {
    const consumptionConfiguration = this.resolveConsumptionConfiguration(configPlugins);
    this.interactionFromMapping = consumptionConfiguration.interactionFromMapping;
    this.interactionToMapping = consumptionConfiguration.interactionToMapping;
    this.setRegionalApiBaseUrl(consumptionConfiguration.regionApiBaseUrl);

    return await this.validateConsumptionRegionConfiguration(consumptionConfiguration.regionApiBaseUrl);
  }

  setRegionalApiBaseUrl(regionUrl?: string | null): void {
    const functionName = 'setRegionalApiBaseUrl';
    const normalizedRegionUrl = this.normalizeBaseUrl(regionUrl);
    if (!normalizedRegionUrl) {
      this.regionalApiBaseUrl = null;
      this.loggerService.logger.logDebug(`${functionName}: Region URL missing/invalid, using default API URL ${this.API_URL}`);
      return;
    }

    this.regionalApiBaseUrl = normalizedRegionUrl;
    this.loggerService.logger.logDebug(`${functionName}: Using regional API URL ${this.regionalApiBaseUrl}`);
  }

  private async validateConsumptionRegionConfiguration(regionApiBaseUrl: string | null): Promise<boolean> {
    const functionName = 'validateConsumptionRegionConfiguration';

    if (!this.apiService.isConsumptionBasedUser()) {
      return true;
    }

    const normalizedRegionUrl = this.normalizeBaseUrl(regionApiBaseUrl);
    if (!normalizedRegionUrl) {
      this.loggerService.logger.logWarning(`${functionName}: Region URL is missing/invalid for a consumption-based user.`);
      await this.agentLockoutService.lockout(
        AgentLockoutReason.InvalidRegionConfiguration,
        'Consumption-based user is missing a valid Agent Configuration Region URL.'
      );
      return false;
    }

    const normalizedApiVersion = (this.API_VERSION || 'v1').replace(/^\/+|\/+$/g, '');
    const statusCheckEndpoint = `${normalizedRegionUrl}/${normalizedApiVersion}/Api/StatusCheck/IsActive`;

    try {
      const isActive = await this.http.get<boolean>(statusCheckEndpoint, { withCredentials: true }).toPromise();
      if (isActive === true) {
        return true;
      }

      this.loggerService.logger.logWarning(
        `${functionName}: Region StatusCheck responded as inactive for endpoint ${statusCheckEndpoint}. response=${JSON.stringify(isActive)}`
      );
    } catch (error) {
      this.loggerService.logger.logError(`${functionName}: Failed region StatusCheck validation for endpoint ${statusCheckEndpoint}: ${JSON.stringify(error)}`);
    }

    await this.agentLockoutService.lockout(
      AgentLockoutReason.InvalidRegionConfiguration,
      `Consumption-based user failed Region StatusCheck validation. endpoint=${statusCheckEndpoint}`
    );

    return false;
  }

  private resolveConsumptionConfiguration(configPlugins: any[]): IConsumptionConfiguration {
    let regionApiBaseUrl: string | null = null;
    let interactionFromMapping: string[] | null = null;
    let interactionToMapping: string[] | null = null;

    for (const configPlugin of configPlugins || []) {
      const consumptionVariables = this.getConsumptionNodeVariables(configPlugin);

      if (!regionApiBaseUrl) {
        regionApiBaseUrl =
          this.getConfigVariableValue(consumptionVariables, 'Region') ||
          this.getConfigVariableValue(configPlugin?.Configuration?.variables, 'Region') ||
          this.getConfigVariableValue(configPlugin?.variables, 'Region');
      }

      if (!interactionFromMapping) {
        interactionFromMapping = this.getConfigVariableArray(consumptionVariables, 'InteractionFromMapping');
      }

      if (!interactionToMapping) {
        interactionToMapping = this.getConfigVariableArray(consumptionVariables, 'InteractionToMapping');
      }

      if (regionApiBaseUrl && interactionFromMapping && interactionToMapping) {
        break;
      }
    }

    return {
      regionApiBaseUrl,
      interactionFromMapping: interactionFromMapping || [...DEFAULT_INTERACTION_FROM_MAPPING],
      interactionToMapping: interactionToMapping || [...DEFAULT_INTERACTION_TO_MAPPING]
    };
  }

  private getConsumptionNodeVariables(configPlugin: any): any {
    if (!configPlugin || typeof configPlugin !== 'object') {
      return null;
    }

    const consumptionNodeKey = Object.keys(configPlugin).find((key) => key.toLowerCase() === 'consumption');
    if (!consumptionNodeKey) {
      return null;
    }

    return configPlugin[consumptionNodeKey]?.variables || null;
  }

  private getConfigVariableValue(variables: any, variableName: string): string | null {
    if (!variables || typeof variables !== 'object') {
      return null;
    }

    const matchingKey = Object.keys(variables).find((key) => key.toLowerCase() === variableName.toLowerCase());
    if (!matchingKey) {
      return null;
    }

    const value = variables[matchingKey];
    if (value == null) {
      return null;
    }

    const normalizedValue = value.toString().trim();
    return normalizedValue ? normalizedValue : null;
  }

  private getConfigVariableArray(variables: any, variableName: string): string[] | null {
    if (!variables || typeof variables !== 'object') {
      return null;
    }

    const matchingKey = Object.keys(variables).find((key) => key.toLowerCase() === variableName.toLowerCase());
    if (!matchingKey) {
      return null;
    }

    return this.normalizeStringArray(variables[matchingKey]);
  }

  private normalizeStringArray(rawValue: any): string[] | null {
    const values = this.extractStringArrayValues(rawValue);
    if (!values || values.length === 0) {
      return null;
    }

    const uniqueValues: string[] = [];
    const seen = new Set<string>();

    for (const value of values) {
      const normalizedValue = value.trim();
      if (!normalizedValue) {
        continue;
      }

      const dedupeKey = normalizedValue;
      if (seen.has(dedupeKey)) {
        continue;
      }

      seen.add(dedupeKey);
      uniqueValues.push(normalizedValue);
    }

    return uniqueValues.length > 0 ? uniqueValues : null;
  }

  private extractStringArrayValues(rawValue: any): string[] | null {
    if (rawValue == null) {
      return null;
    }

    if (Array.isArray(rawValue)) {
      return rawValue.map((item) => this.getFieldValueAsString(item)).filter((item) => !!item);
    }

    if (typeof rawValue === 'string') {
      const trimmedValue = rawValue.trim();
      if (!trimmedValue) {
        return null;
      }

      if (trimmedValue.startsWith('[') && trimmedValue.endsWith(']')) {
        try {
          const parsedArray = JSON.parse(trimmedValue);
          if (Array.isArray(parsedArray)) {
            return parsedArray.map((item) => this.getFieldValueAsString(item)).filter((item) => !!item);
          }
        } catch {
          // Keep default parsing behavior below.
        }
      }

      if (trimmedValue.includes(',')) {
        return trimmedValue
          .split(',')
          .map((item) => item.trim())
          .filter((item) => !!item);
      }

      return [trimmedValue];
    }

    if (typeof rawValue === 'object') {
      if (rawValue['!value'] != null) {
        return this.extractStringArrayValues(rawValue['!value']);
      }

      if (rawValue.value != null) {
        return this.extractStringArrayValues(rawValue.value);
      }

      if (rawValue.Value != null) {
        return this.extractStringArrayValues(rawValue.Value);
      }

      const objectValues = Object.values(rawValue);
      if (objectValues.length > 0) {
        return objectValues.map((item) => this.getFieldValueAsString(item)).filter((item) => !!item);
      }

      return null;
    }

    const singleValue = this.getFieldValueAsString(rawValue);
    return singleValue ? [singleValue] : null;
  }

  handleInteractionConsumption(message?: { data?: any[] }): void {
    const functionName = 'handleInteractionConsumption';

    if (!this.apiService.isConsumptionBasedUser()) {
      return;
    }

    try {
      const interaction = message?.data?.[0];
      if (!interaction || !interaction.scenarioId) {
        return;
      }
      const scenarioId = interaction.scenarioId.toString();

      const isDisconnectedState =
        interaction.state === INTERACTION_STATES.Disconnected || (typeof interaction.state === 'string' && interaction.state.toLowerCase() === 'disconnected');
      const interactionId = interaction.interactionId ? interaction.interactionId.toString() : null;

      if (interactionId) {
        this.updateScenarioInteractionState(scenarioId, interactionId, isDisconnectedState);
      }

      if (isDisconnectedState) {
        this.onInteractionDisconnect(scenarioId);

        if (this.shouldLockoutAfterDisconnect(scenarioId)) {
          this.triggerCreditsDepletedLockout(scenarioId).catch((error) => {
            this.loggerService.logger.logError(`${functionName}: Error during credits-depleted lockout flow: ${JSON.stringify(error)}`);
          });
        }

        return;
      }

      if (!interactionId) {
        return;
      }

      const channel =
        typeof interaction.channelType === 'number'
          ? CHANNEL_TYPES[interaction.channelType] ?? ''
          : typeof interaction.channelType === 'string'
          ? interaction.channelType
          : '';

      const to = this.getInteractionField(interaction, this.interactionToMapping);
      const from = this.getInteractionField(interaction, this.interactionFromMapping);

      this.sendInteractionConsumption(scenarioId, interactionId, to, from, channel, new Date().toISOString()).catch((error) => {
        this.loggerService.logger.logError(`${functionName}: ${JSON.stringify(error)}`);
      });
    } catch (error) {
      this.loggerService.logger.logError(`${functionName}: ${JSON.stringify(error)}`);
    }
  }

  private loadFromStorage(): void {
    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const obj = JSON.parse(stored);
        if (obj && typeof obj === 'object') {
          Object.entries(obj).forEach(([key, value]: [string, any]) => {
            if (value && value.timestamp) {
              this.consumptionMap.set(key, {
                scenarioId: key,
                timestamp: value.timestamp
              });
            }
          });
        }
      }
    } catch (error) {
      this.loggerService.logger.logError(`InteractionConsumptionService: Error loading from storage: ${JSON.stringify(error)}`);
    }
  }

  private saveToStorage(): void {
    try {
      const obj: any = {};
      this.consumptionMap.forEach((value, key) => {
        obj[key] = {
          scenarioId: value.scenarioId,
          timestamp: value.timestamp
        };
      });
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(obj));
    } catch (error) {
      this.loggerService.logger.logError(`InteractionConsumptionService: Error saving to storage: ${JSON.stringify(error)}`);
    }
  }

  async sendInteractionConsumption(scenarioId: string, interactionId: string, to: string, from: string, channel: string, timestamp: string): Promise<boolean> {
    const functionName = 'sendInteractionConsumption';

    try {
      if (!scenarioId) {
        this.loggerService.logger.logWarning(`${functionName}: scenarioId is required`);
        return false;
      }

      // Check if scenarioId is already in the map
      if (this.consumptionMap.has(scenarioId)) {
        this.loggerService.logger.logDebug(`${functionName}: scenarioId ${scenarioId} already exists in consumption tracking`);
        return false;
      }

      const payload = {
        scenarioId,
        interactionId,
        to,
        from,
        channel,
        timestamp
      };
      const endpoint = this.getAddInteractionEndpoint();

      this.loggerService.logger.logDebug(`${functionName}: Sending interaction consumption to ${endpoint}: ${JSON.stringify(payload)}`);

      const result = await this.http.post(endpoint, payload, { withCredentials: true }).toPromise<any>();
      const response = result as IAddInteractionResponse;
      const status = response?.status || response?.Status;

      if (status === 'added') {
        this.loggerService.logger.logDebug(`${functionName}: Successfully recorded consumption for scenarioId: ${scenarioId}`);

        // Store the timestamp for this scenarioId
        const entry: IConsumptionEntry = {
          scenarioId,
          timestamp
        };
        this.consumptionMap.set(scenarioId, entry);
        this.saveToStorage();
        if (this.isCreditsDepleted(response)) {
          this.lockoutPendingScenarios.add(scenarioId);
          this.loggerService.logger.logWarning(
            `${functionName}: Consumption increment depleted credits for scenarioId ${scenarioId}. Logout will occur once all interactions have disconnected.`
          );
          if (this.shouldLockoutAfterDisconnect(scenarioId)) {
            await this.triggerCreditsDepletedLockout(scenarioId);
          }
        }

        return true;
      } else {
        this.loggerService.logger.logWarning(`${functionName}: Unexpected response: ${JSON.stringify(response)}`);
        return false;
      }
    } catch (error) {
      this.loggerService.logger.logError(`${functionName}: Error sending interaction consumption: ${JSON.stringify(error)}`);
      return false;
    }
  }

  private isCreditsDepleted(response: IAddInteractionResponse): boolean {
    const responseCreditsDepleted = response?.creditsDepleted ?? response?.CreditsDepleted;
    if (responseCreditsDepleted === true) {
      return true;
    }

    const rawRemainingCredits = response?.remainingCredits ?? response?.RemainingCredits;
    if (rawRemainingCredits == null) {
      return false;
    }

    const parsedRemainingCredits = Number(rawRemainingCredits);
    if (Number.isNaN(parsedRemainingCredits)) {
      return false;
    }

    return parsedRemainingCredits <= 0;
  }

  private updateScenarioInteractionState(scenarioId: string, interactionId: string, isDisconnected: boolean): void {
    if (!this.scenarioInteractionStates.has(scenarioId)) {
      this.scenarioInteractionStates.set(scenarioId, new Map<string, boolean>());
    }

    const interactionStateMap = this.scenarioInteractionStates.get(scenarioId);
    interactionStateMap.set(interactionId, isDisconnected);
  }

  private shouldLockoutAfterDisconnect(scenarioId: string): boolean {
    if (!this.lockoutPendingScenarios.has(scenarioId)) {
      return false;
    }

    const interactionStateMap = this.scenarioInteractionStates.get(scenarioId);
    if (!interactionStateMap || interactionStateMap.size === 0) {
      return true;
    }

    for (const isDisconnected of interactionStateMap.values()) {
      if (!isDisconnected) {
        return false;
      }
    }

    return true;
  }

  private async triggerCreditsDepletedLockout(scenarioId: string): Promise<void> {
    this.lockoutPendingScenarios.delete(scenarioId);
    this.scenarioInteractionStates.delete(scenarioId);

    await this.agentLockoutService.lockout(AgentLockoutReason.CreditsExhaustedAfterCall, `Credits depleted during active scenario. scenarioId=${scenarioId}`);
  }

  onInteractionDisconnect(scenarioId: string): void {
    const functionName = 'onInteractionDisconnect';

    try {
      this.loggerService.logger.logDebug(`${functionName}: Interaction disconnected for scenarioId: ${scenarioId}`);

      // Clear any existing timeout for this scenarioId
      const existingTimeout = this.clearTimeouts.get(scenarioId);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
        this.clearTimeouts.delete(scenarioId);
      }

      // Set a new timeout to clear the entry after the configured time
      const timeout = setTimeout(() => {
        this.clearTimeouts.delete(scenarioId);
        const interactionStateMap = this.scenarioInteractionStates.get(scenarioId);
        if (interactionStateMap && Array.from(interactionStateMap.values()).some((isDisconnected) => !isDisconnected)) {
          return;
        }
        this.consumptionMap.delete(scenarioId);
        this.lockoutPendingScenarios.delete(scenarioId);
        this.scenarioInteractionStates.delete(scenarioId);
        this.saveToStorage();
        this.loggerService.logger.logDebug(`${functionName}: Cleared consumption entry for scenarioId: ${scenarioId} after timeout`);
      }, this.CLEAR_TIMEOUT_MS);

      this.clearTimeouts.set(scenarioId, timeout);
    } catch (error) {
      this.loggerService.logger.logError(`${functionName}: Error handling disconnect: ${JSON.stringify(error)}`);
    }
  }

  isScenarioIdTracked(scenarioId: string): boolean {
    return this.consumptionMap.has(scenarioId);
  }

  private getInteractionField(interaction: any, candidateFields: string[]): string {
    const fields = interaction?.details?.fields;
    for (const field of candidateFields) {
      const topLevelValue = this.getFieldValueAsString(interaction?.[field]);
      if (topLevelValue) {
        return topLevelValue;
      }

      const exactFieldValue = this.getFieldValueAsString(fields?.[field]);
      if (exactFieldValue) {
        return exactFieldValue;
      }

      if (fields) {
        const matchingKey = Object.keys(fields).find((key) => key.toLowerCase() === field.toLowerCase());
        if (matchingKey) {
          const caseInsensitiveFieldValue = this.getFieldValueAsString(fields[matchingKey]);
          if (caseInsensitiveFieldValue) {
            return caseInsensitiveFieldValue;
          }
        }
      }
    }

    return '';
  }

  private getFieldValueAsString(value: any): string {
    if (value == null) {
      return '';
    }

    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      return String(value);
    }

    if (typeof value === 'object') {
      if (value.Value != null) {
        return String(value.Value);
      }

      if (value.value != null) {
        return String(value.value);
      }
    }

    return '';
  }

  private getAddInteractionEndpoint(): string {
    const baseUrl = this.regionalApiBaseUrl || this.API_URL; // ToDo: If this is not present we need to lock the agent out of DaVinci and prevent calls
    const normalizedBaseUrl = this.normalizeBaseUrl(baseUrl);
    const normalizedApiVersion = (this.API_VERSION || 'v1').replace(/^\/+|\/+$/g, '');
    return `${normalizedBaseUrl}/${normalizedApiVersion}/Api/InteractionConsumption/AddInteraction`;
  }

  private normalizeBaseUrl(url?: string | null): string | null {
    if (!url) {
      return null;
    }

    const trimmed = url.trim();
    if (!trimmed) {
      return null;
    }

    try {
      const parsed = new URL(trimmed);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        return null;
      }

      return parsed.toString().replace(/\/+$/, '');
    } catch {
      return null;
    }
  }

  clear(): void {
    this.consumptionMap.clear();
    this.lockoutPendingScenarios.clear();
    this.scenarioInteractionStates.clear();
    this.clearTimeouts.forEach((timeout) => clearTimeout(timeout));
    this.clearTimeouts.clear();
    sessionStorage.removeItem(this.STORAGE_KEY);
  }
}
